import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ApplicationCategoriesProps, ApplicationsProps } from '@config/useApps';
import SessionContext from '@providers/Session/SessionContext';
import { Button, Menu, Radio, RadioChangeEvent, Row, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import rolesConstants from '@utils/rolesConstants';

import { WarningOutlined } from '@ant-design/icons';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import useSwr from '@hooks/useSwr';
import { RouteItemProps } from '@config/routes/types';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import * as S from './styles';

interface SectionPagesProps {
  selectedApplication: ApplicationsProps | undefined;
  eventsAlertsData: any;
  alreadyViewEvents: any;
  setAlreadyViewEvents: any;
  setUserAttributes: any;
}

const SectionPages = ({
  selectedApplication,
  eventsAlertsData,
  alreadyViewEvents,
  setAlreadyViewEvents,
  setUserAttributes,
}: SectionPagesProps) => {
  const { t } = useTranslation('main');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isRoot, isNotChildOfRoot, customer, getAppByName } =
    useContext(SessionContext);
  const { keycloak } = useContext(KeycloakContext);
  const customerTypeLogged = keycloak.idTokenParsed?.customerType;
  const isEndCustomer = customerTypeLogged === 'CUSTOMER';

  const { data: dataSmsConfig } = useSwr(
    selectedApplication?.name === ApplicationsConstants.SMS
      ? `/service-proxy/sms-config`
      : null,
  );

  const [deviceType, setDeviceType] = useState<'mobile-network' | 'lorawan'>(
    'mobile-network',
  );

  const hasLora = Boolean(getAppByName(ApplicationsConstants.LORAWAN));

  useEffect(() => {
    if (!hasLora) return;

    const isLoraRoute = pathname.includes('lorawan');

    if (isLoraRoute) {
      setDeviceType('lorawan');
    } else {
      setDeviceType('mobile-network');
    }
  }, [pathname, getAppByName, hasLora]);

  const handlePagesWithoutDetails = useCallback(
    (pages: ApplicationCategoriesProps['pages']) =>
      pages?.filter(page => !page?.isDetails),
    [],
  );

  const hasMobileNetworkPages = useMemo(
    () =>
      selectedApplication?.categories?.some(category => {
        const pagesWithoutDetails = handlePagesWithoutDetails(category?.pages);

        return pagesWithoutDetails?.some(
          page => !page?.isLoraWan && !page?.isCommon,
        );
      }),
    [handlePagesWithoutDetails, selectedApplication?.categories],
  );

  const hasLoraWanPages = useMemo(
    () =>
      (hasLora || isEndCustomer) &&
      selectedApplication?.hasLorawan &&
      selectedApplication?.categories?.some(category => {
        const pagesWithoutDetails = handlePagesWithoutDetails(category?.pages);

        return pagesWithoutDetails?.some(page => page?.isLoraWan);
      }),
    [
      handlePagesWithoutDetails,
      hasLora,
      isEndCustomer,
      selectedApplication?.categories,
      selectedApplication?.hasLorawan,
    ],
  );

  const treatedCategories = useMemo(() => {
    let categories = selectedApplication?.categories;
    if (selectedApplication?.hasLorawan) {
      categories = categories?.map(category => ({
        ...category,
        pages: category?.pages?.filter(page => {
          if (deviceType === 'lorawan') {
            return (
              (page?.isLoraWan && !page?.isDetails) ||
              (page?.isCommon && !page?.isDetails)
            );
          }
          return !page?.isLoraWan && !page?.isDetails;
        }),
      }));
    }
    return categories?.filter(category => category?.pages?.length > 0);
  }, [
    deviceType,
    selectedApplication?.categories,
    selectedApplication?.hasLorawan,
  ]);

  const hasMoreThanOneCategory = useMemo(
    () => treatedCategories && treatedCategories?.length > 1,
    [treatedCategories],
  );

  const firstCategory = useMemo(
    () => treatedCategories?.[0],
    [treatedCategories],
  );

  const handlePages = useCallback(
    (pages?: RouteItemProps[], categoryName?: string) =>
      pages?.map((page: RouteItemProps) => {
        if (page.requiredApps && page.requiredApps.length > 0) {
          const hasRequiredApps =
            page.requireAll === false
              ? page.requiredApps.some(app =>
                  customer?.customerLogged?.listContractTypes?.some(
                    (type: string) => type.toLowerCase() === app.toLowerCase(),
                  ),
                )
              : page.requiredApps.every(app =>
                  customer?.customerLogged?.listContractTypes?.some(
                    (type: string) => type.toLowerCase() === app.toLowerCase(),
                  ),
                );

          if (!isRoot && !hasRequiredApps) {
            return null;
          }
        }

        if (
          (page.role === rolesConstants.MANAGE_PERSONALIZATION &&
            isNotChildOfRoot) ||
          (page.externalLink && isNotChildOfRoot)
        ) {
          return false;
        }

        let pagePathname = '';
        if (!page.externalLink) {
          let basePath = `/${selectedApplication?.name}`;

          if (selectedApplication?.name !== categoryName) {
            const lorawanPath = page?.isLoraWan ? 'lorawan' : 'mobile-network';
            const isCommonPath = page?.isCommon ? deviceType : lorawanPath;
            const deviceBasePath = selectedApplication?.hasLorawan
              ? `${isCommonPath}/`
              : '';

            basePath = `${basePath}/${deviceBasePath}${categoryName}`;
          }

          pagePathname = `${basePath}/${page.path}`;
        }

        const pageName = t(`menu.${categoryName}.${page.name ?? page.path}`);
        const rootCannotSee = page.rootCannotSee && isRoot;
        const isSms = categoryName === ApplicationsConstants.SMS;
        const haveConfig =
          dataSmsConfig?.hasApiSecret ||
          dataSmsConfig?.hasApiToken ||
          dataSmsConfig?.apiUrl;
        if (isSms && page.path !== 'configuration' && !haveConfig) {
          return (
            <Tooltip title={t('not-have-sms-config')} mouseEnterDelay={0.5}>
              <Menu.Item
                key={pagePathname}
                icon={page.icon}
                className="root-cannot-see-page"
              >
                {pageName}
              </Menu.Item>
            </Tooltip>
          );
        }
        if (!page.isDetails) {
          return rootCannotSee ? (
            <Tooltip title={t('root-cannot-see')} mouseEnterDelay={0.5}>
              <Menu.Item
                key={pagePathname}
                icon={page.icon}
                className="root-cannot-see-page"
              >
                {pageName}
              </Menu.Item>
            </Tooltip>
          ) : (
            <Menu.Item
              key={pagePathname}
              icon={page.icon}
              style={
                page.externalLink
                  ? {
                      border: '1px dotted var(--primary-color)',
                      color: 'var(--primary-color)',
                    }
                  : undefined
              }
              onClick={() => {
                if (page.externalLink) {
                  let domain = new URL(window.location.href).hostname;
                  if (
                    domain === 'localhost' &&
                    process.env.REACT_APP_HML_HOSTNAME
                  ) {
                    domain = process.env.REACT_APP_HML_HOSTNAME;
                  }
                  const treatedPath = page.path.replace('{domain}', domain);
                  window.open(treatedPath, '_blank');
                } else {
                  if (page.path === 'events') {
                    setAlreadyViewEvents(true);
                    setUserAttributes({
                      alreadyViewEvents: 'false',
                    });
                  }
                  navigate(pagePathname);
                }
              }}
            >
              {pageName}
              {page.path === 'events' &&
                eventsAlertsData?.items?.length > 0 &&
                !alreadyViewEvents && (
                  <Tooltip title={t('attributes:newAlert')}>
                    <Button
                      type="link"
                      icon={
                        <WarningOutlined className="custom-alert-warning" />
                      }
                    />
                  </Tooltip>
                )}
            </Menu.Item>
          );
        }
        return undefined;
      }),
    [
      isNotChildOfRoot,
      t,
      isRoot,
      dataSmsConfig?.hasApiSecret,
      dataSmsConfig?.hasApiToken,
      dataSmsConfig?.apiUrl,
      customer?.customerLogged?.listContractTypes,
      selectedApplication?.name,
      selectedApplication?.hasLorawan,
      deviceType,
      eventsAlertsData?.items?.length,
      alreadyViewEvents,
      navigate,
      setAlreadyViewEvents,
      setUserAttributes,
    ],
  );

  const subMenu = useMemo(
    () =>
      treatedCategories?.map(({ categoryName, pages }) => {
        if (categoryName === 'administrative') {
          const hasOnlyPersonalization =
            pages?.length === 1 &&
            pages?.[0]?.role === rolesConstants.MANAGE_PERSONALIZATION;

          if (hasOnlyPersonalization && isNotChildOfRoot) {
            return false;
          }
        }
        return (
          <Menu.ItemGroup
            key={categoryName}
            title={t(`menu.${categoryName}.title`)}
          >
            {handlePages(pages, categoryName)}
          </Menu.ItemGroup>
        );
      }),
    [treatedCategories, t, handlePages, isNotChildOfRoot],
  );

  const handleChangeDeviceType = useCallback(
    ({ target: { value } }: RadioChangeEvent) => {
      setDeviceType(value);
    },
    [],
  );

  return (
    <S.Pages>
      {hasLoraWanPages && (
        <S.DeviceTypeWrapper>
          <Row justify="center">
            <Radio.Group
              onChange={handleChangeDeviceType}
              value={deviceType}
              defaultValue="mobile-network"
              size="small"
            >
              <Radio.Button
                value="mobile-network"
                style={{
                  pointerEvents: !hasLoraWanPages ? 'none' : 'initial',
                }}
              >
                {t('device-types.mobile-network')}
              </Radio.Button>
              <Radio.Button
                value="lorawan"
                style={{
                  pointerEvents: !hasMobileNetworkPages ? 'none' : 'initial',
                }}
              >
                {t('device-types.lorawan')}
              </Radio.Button>
            </Radio.Group>
          </Row>
        </S.DeviceTypeWrapper>
      )}
      <S.PagesMenuContainer onlyOneCategory={!hasMoreThanOneCategory}>
        <Menu theme="dark" mode="inline" selectedKeys={[pathname]}>
          {(() => {
            if (treatedCategories && hasMoreThanOneCategory) {
              return subMenu;
            }

            if (
              firstCategory &&
              firstCategory.categoryName !== selectedApplication?.name
            ) {
              return (
                <Menu.ItemGroup
                  key={firstCategory.categoryName}
                  title={t(`menu.${firstCategory.categoryName}.title`)}
                >
                  {handlePages(
                    firstCategory?.pages,
                    firstCategory?.categoryName,
                  )}
                </Menu.ItemGroup>
              );
            }

            return handlePages(
              firstCategory?.pages,
              firstCategory?.categoryName,
            );
          })()}
        </Menu>
      </S.PagesMenuContainer>
    </S.Pages>
  );
};

export default SectionPages;
